import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faChartLine,
  faChartMixed,
  faEdit,
  faShieldHalved,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { compact, times } from "lodash-es";

import {
  type DashboardListItem,
  defaultDashboardId,
  useListDashboardsQuery,
} from "src/api/analytics-dashboard/dashboard";
import { isPerformanceDashboard } from "src/api/analytics-dashboard/fixedDashboards";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { AVAILABLE_ICONS } from "src/cases/templates/utils";
import { Realm } from "src/clients/analytics-dashboard";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";
import { SubHeader, SubHeaderTabs } from "src/layout/SubHeader";

const DashboardTab: React.FC<{
  label: string;
  icon?: IconProp;
  active: boolean;
  dataLoc?: string;
  onClick: () => void;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}> = ({
  label,
  icon,
  active,
  dataLoc,
  onClick,
  onEditRequest,
  onDeleteRequest,
}) => (
  <div
    className="relative inline-flex h-full items-center"
    data-active={active}
    data-loc={dataLoc}
  >
    <button
      className="flex h-full items-center px-1 font-inter-semibold-13px text-gray-800"
      data-active={active}
      type="button"
      onClick={onClick}
    >
      <div className="flex items-center gap-x-1.5">
        {icon && <Icon icon={icon} size="xs" />}
        <span className="whitespace-nowrap">{label}</span>
      </div>
    </button>
    {(onEditRequest || onDeleteRequest) && (
      <EllipsisOptionsDropdown
        dataLoc={`${dataLoc}-menu`}
        elements={compact([
          onEditRequest && {
            key: "Edit dashboard details",
            icon: faEdit,
            action: onEditRequest,
          },
          onDeleteRequest && {
            key: "Delete dashboard",
            icon: faTrashCan,
            action: onDeleteRequest,
          },
        ])}
        iconSize="xs"
      />
    )}
    {active && (
      <div className="absolute right-0 bottom-0 left-0 h-[3px] rounded-full bg-indigo-600" />
    )}
  </div>
);

const DashboardTabsLoading: React.FC = () => (
  <div
    className="flex h-full items-center gap-2"
    data-loc="dashboard-tabs-loading"
  >
    {times(3, (index) => (
      <Skeleton key={index} height="h-2/3" width="w-20" />
    ))}
  </div>
);

export const DashboardTabs: React.FC<{
  realm: Realm;
  realmId: string;
  activeDashboardId: string;
  canCreate?: boolean;
  onDashboardChange: (dashboardId: string) => void;
  onCreateRequest?: () => void;
  onEditRequest?: (dashboardId: string) => void;
  onDeleteRequest?: (dashboardId: string) => void;
  children?: React.ReactNode;
}> = ({
  realm,
  realmId,
  activeDashboardId,
  canCreate = true,
  onDashboardChange,
  onCreateRequest,
  onEditRequest,
  onDeleteRequest,
  children,
}) => {
  const { data: dashboards, isLoading } = useListDashboardsQuery(
    realm,
    realmId,
  );

  // The flow realm's default tab is synthesized into the list, but it does have
  // a stored record, so a custom icon has to win over the chart-mixed default.
  // Performance has no record at all, so its icon is fixed.
  const flowDefaultId =
    realm === Realm.FLOWS ? defaultDashboardId(realm, realmId) : undefined;

  const tabIcon = (dashboard: DashboardListItem): IconProp => {
    if (isPerformanceDashboard(realm, dashboard.id)) return faChartLine;
    if (dashboard.icon)
      return AVAILABLE_ICONS[dashboard.icon] ?? faShieldHalved;
    return dashboard.id === flowDefaultId ? faChartMixed : faShieldHalved;
  };

  const tabs = (dashboards ?? []).map((d) => ({
    id: d.id,
    label: d.name ?? "Untitled Dashboard",
    icon: tabIcon(d),
    // Deleting the flow default would drop its stored record and the tab would
    // reappear, emptied, on the next list read.
    canDelete: !isPerformanceDashboard(realm, d.id) && d.id !== flowDefaultId,
  }));

  // Fall back to first tab if the active dashboard is not in the list.
  const resolvedId =
    tabs.some((t) => t.id === activeDashboardId) && activeDashboardId !== ""
      ? activeDashboardId
      : tabs[0]?.id;

  return (
    <SubHeader
      leftSlot={
        <>
          {isLoading && <DashboardTabsLoading />}
          {!isLoading && (
            <SubHeaderTabs dataLoc="dashboard-tabs">
              {tabs.map((tab) => (
                <DashboardTab
                  key={tab.id}
                  active={resolvedId === tab.id}
                  dataLoc={`dashboard-tab-${tab.id}`}
                  icon={tab.icon}
                  label={tab.label}
                  onClick={() => onDashboardChange(tab.id)}
                  onDeleteRequest={
                    onDeleteRequest && tab.canDelete
                      ? () => onDeleteRequest(tab.id)
                      : undefined
                  }
                  onEditRequest={
                    onEditRequest && !isPerformanceDashboard(realm, tab.id)
                      ? () => onEditRequest(tab.id)
                      : undefined
                  }
                />
              ))}
            </SubHeaderTabs>
          )}
          {canCreate && onCreateRequest && (
            <span className="flex-1">
              <Tooltip
                dataLoc="create-dashboard-button-tooltip"
                delay={350}
                title="Create dashboard"
                asChild
              >
                <button
                  className="p-3.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:ring-inset disabled:cursor-not-allowed disabled:bg-gray-50"
                  data-loc="create-dashboard-button"
                  type="button"
                  onClick={onCreateRequest}
                >
                  <Icon icon={faPlus} size="xs" />
                </button>
              </Tooltip>
            </span>
          )}
        </>
      }
    >
      {children}
    </SubHeader>
  );
};
